/**
 * SCRUM-131 — vinculación pública para Digital Signage.
 *
 * /signage/pair — el player (TV, tablet, mini-PC) abre esta URL, ingresa el
 * código corto que muestra el hub del tenant y queda vinculado al display.
 *
 * IMPORTANTE multi-tenant: NO redirigir en silencio al token viejo de
 * localStorage. Si el staff pasa de Frosty → Bebidas con el mismo browser,
 * un auto-redirect al token anterior mostraría el contenido del tenant viejo.
 *
 * Reglas:
 *  - ?code=XXXX → siempre reclama ese código y sobrescribe el token guardado.
 *  - Sin code + token existente → ofrecer Continuar vs Vincular otra.
 *  - Claim exitoso → guarda token y va a /signage/$token.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const TOKEN_STORAGE_KEY = "signage.playerToken";

export const Route = createFileRoute("/signage/pair")({
  validateSearch: (s: Record<string, unknown>) => ({
    code: typeof s.code === "string" && s.code.trim() ? s.code.trim().toUpperCase() : undefined,
    /** Fuerza el formulario aunque haya token guardado. */
    rebind: s.rebind === "1" || s.rebind === true || s.rebind === "true" ? true : undefined,
  }),
  component: SignagePairPage,
});

type Mode = "form" | "resume" | "claiming";

function SignagePairPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [code, setCode] = useState(search.code ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>("form");
  const [existingToken, setExistingToken] = useState<string | null>(null);

  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      stored = null;
    }
    setExistingToken(stored);

    // Código en URL (desde el hub): claim automático, no usar token viejo.
    if (search.code) {
      setCode(search.code);
      setMode("form");
      void claimWithCode(search.code);
      return;
    }

    if (stored && !search.rebind) {
      setMode("resume");
      return;
    }

    setMode("form");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- claim sólo al montar / cambiar code de search
  }, [search.code, search.rebind]);

  const claimWithCode = async (raw: string) => {
    const pairCode = raw.trim().toUpperCase();
    if (pairCode.length < 4) {
      setErr("Ingresá el código completo");
      return;
    }
    setBusy(true);
    setMode("claiming");
    setErr(null);
    try {
      const res = await fetch("/api/signage/claim", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pairCode }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        playerToken?: string;
        name?: string;
        error?: string;
      };
      if (!res.ok || !data.playerToken) {
        setMode("form");
        setErr(
          data.error ??
            (res.status === 410
              ? "Código vencido. Generá uno nuevo en /app/pantallas."
              : "No se pudo vincular. Revisá el código e intentá de nuevo."),
        );
        return;
      }
      try {
        // Sobrescribe siempre — evita quedar pegado al tenant anterior.
        window.localStorage.setItem(TOKEN_STORAGE_KEY, data.playerToken);
      } catch {
        /* sin localStorage seguimos con redirect */
      }
      void navigate({
        to: "/signage/$token",
        params: { token: data.playerToken },
        replace: true,
      });
    } catch {
      setMode("form");
      setErr("Sin conexión");
    } finally {
      setBusy(false);
    }
  };

  const clearStoredAndShowForm = () => {
    try {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
      /* noop */
    }
    setExistingToken(null);
    setCode("");
    setErr(null);
    setMode("form");
    void navigate({ to: "/signage/pair", search: { rebind: true }, replace: true });
  };

  if (mode === "resume" && existingToken) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center bg-slate-950 px-6 text-slate-100">
        <div className="mb-1 text-xs font-medium uppercase tracking-widest text-slate-500">
          Digital Signage
        </div>
        <h1 className="text-2xl font-medium tracking-tight">Pantalla ya vinculada</h1>
        <p className="mt-2 max-w-sm text-center text-sm text-slate-400">
          Este dispositivo tiene una pantalla guardada. Si cambiaste de comercio
          (p. ej. de Frosty a Bebidas), vinculá con el código nuevo.
        </p>
        <button
          type="button"
          className="mt-6 rounded-lg bg-slate-100 px-8 py-3 text-sm font-medium text-slate-900"
          onClick={() =>
            void navigate({
              to: "/signage/$token",
              params: { token: existingToken },
              replace: true,
            })
          }
        >
          Continuar con la pantalla guardada
        </button>
        <button
          type="button"
          className="mt-3 rounded-lg border border-slate-600 px-8 py-3 text-sm font-medium text-slate-100 hover:bg-slate-900"
          onClick={clearStoredAndShowForm}
        >
          Vincular con otro código (otro comercio)
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-slate-950 px-6 text-slate-100">
      <div className="mb-1 text-xs font-medium uppercase tracking-widest text-slate-500">
        Digital Signage
      </div>
      <h1 className="text-2xl font-medium tracking-tight">Vincular pantalla</h1>
      <p className="mt-2 max-w-sm text-center text-sm text-slate-400">
        Ingresá el código que aparece en el hub del comercio (/app/pantallas).
        <br />
        El código dura 60 minutos. Si venció, regeneralo desde el hub.
      </p>
      <input
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        maxLength={8}
        className="mt-6 w-full max-w-xs rounded-lg border border-slate-700 bg-slate-900/80 px-4 py-3 text-center text-2xl tracking-[0.3em] uppercase text-slate-100 outline-none placeholder:text-slate-600 focus:border-slate-500"
        placeholder="ABC123"
        autoComplete="off"
        autoFocus
        disabled={busy}
      />
      {err ? <p className="mt-3 max-w-sm text-center text-sm text-rose-300">{err}</p> : null}
      <button
        type="button"
        disabled={busy || code.trim().length < 4}
        onClick={() => void claimWithCode(code)}
        className="mt-6 rounded-lg bg-slate-100 px-8 py-3 text-sm font-medium text-slate-900 disabled:opacity-40"
      >
        {busy || mode === "claiming" ? "Vinculando…" : "Vincular"}
      </button>
      {existingToken ? (
        <button
          type="button"
          className="mt-4 text-xs text-slate-500 underline-offset-2 hover:text-slate-300 hover:underline"
          onClick={() =>
            void navigate({
              to: "/signage/$token",
              params: { token: existingToken },
            })
          }
        >
          Volver a la pantalla guardada anterior
        </button>
      ) : null}
      <p className="mt-8 max-w-sm text-center text-xs text-slate-500">
        Cada código pertenece a un solo comercio. Si cambiaste de tienda, usá el
        código nuevo: reemplaza la vinculación anterior en este dispositivo.
      </p>
    </div>
  );
}
